// frontend/src/components/employee/availability/availabilityUtils.js

import { resolveScheduleForDate } from "../../scheduling/scheduleSettingsUtils";

export const DISABLED_DOW = new Set([0, 5]); // Sunday=0, Friday=5
export const START_MINUTE = 9 * 60;
export const END_MINUTE = 22 * 60; // 10 PM
export const TIME_STEP_MINUTES = 30;

export function isDisabledDay(date, dayConfigs = null) {
  const key = ymd(date);
  const cfg = dayConfigs?.[key];
  if (cfg) {
    return !!resolveScheduleForDate(date, cfg).isClosed;
  }
  return DISABLED_DOW.has(date.getDay());
}

export function startOfMonth(d) {
  return new Date(d.getFullYear(), d.getMonth(), 1);
}

export function endOfMonthExclusive(d) {
  return new Date(d.getFullYear(), d.getMonth() + 1, 1);
}

export function addMonths(d, n) {
  return new Date(d.getFullYear(), d.getMonth() + n, 1);
}

export function fmtMonthTitle(d) {
  return d.toLocaleString(undefined, { month: "long", year: "numeric" });
}

export function fmtTimeLabel(minuteOfDay) {
  const safeMinute = Math.max(0, Number(minuteOfDay) || 0);
  const hour24 = Math.floor(safeMinute / 60) % 24;
  const minute = safeMinute % 60;
  const ampm = hour24 >= 12 ? "PM" : "AM";
  const h = hour24 % 12 === 0 ? 12 : hour24 % 12;
  return `${h}:${String(minute).padStart(2, "0")} ${ampm}`;
}

export function buildTimeOptions(step = TIME_STEP_MINUTES) {
  const opts = [];
  for (let minute = START_MINUTE; minute <= END_MINUTE; minute += step) opts.push(minute);
  return opts;
}

export function resolveDayTimelineHours(date, dayConfigs = null) {
  const key = ymd(date);
  const cfg = dayConfigs?.[key];
  const resolved = resolveScheduleForDate(date, cfg);
  return {
    isClosed: !!resolved.isClosed,
    startMin: resolved.startMin,
    endMin: resolved.endMin,
  };
}

export function ymd(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

export function sameYMD(a, b) {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}

export function atTime(date, minuteOfDay) {
  const safeMinute = Math.max(0, Number(minuteOfDay) || 0);
  const hour24 = Math.floor(safeMinute / 60);
  const minute = safeMinute % 60;
  return new Date(date.getFullYear(), date.getMonth(), date.getDate(), hour24, minute, 0, 0);
}

// Fixed: Dynamic grid size. Returns just enough weeks (4, 5, or 6) to cover the month.
export function monthGridDays(monthDate) {
  const firstDayOfMonth = startOfMonth(monthDate);
  const lastDayOfMonth = new Date(monthDate.getFullYear(), monthDate.getMonth() + 1, 0); // Last day of current month
  
  const startDayOfWeek = firstDayOfMonth.getDay(); // 0 (Sun) to 6 (Sat)
  
  // Start grid at the Sunday strictly before or on the 1st
  const gridStart = new Date(firstDayOfMonth);
  gridStart.setDate(gridStart.getDate() - startDayOfWeek);

  // We need to find how many days to generate to cover the last day of the month
  // Logic: Keep adding weeks (7 days) until the last generated day is >= lastDayOfMonth
  const days = [];
  let current = new Date(gridStart);
  
  // Always generate at least one day loop, but simpler to just do loop
  while (true) {
    days.push(new Date(current));
    
    // If we have completed a full week (multiple of 7) AND we have passed the end of the month, stop.
    // We check length > 0 to avoid stopping immediately
    if (days.length % 7 === 0) {
      // Check if the last day we added is after or same as the actual month end
      const lastAdded = days[days.length - 1];
      if (lastAdded >= lastDayOfMonth) {
        break;
      }
    }
    
    // Prepare next day
    current.setDate(current.getDate() + 1);
  }
  
  return days;
}

export function isSameMonth(d1, d2) {
  return d1.getMonth() === d2.getMonth() && d1.getFullYear() === d2.getFullYear();
}
